import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { bootstrapAuthLogsService, bootstrapCaService } from '../services/api';

const BootstrapAuthLogs = () => {
  const [searchParams] = useSearchParams();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50, totalCount: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ 
    status: '', 
    caId: searchParams.get('caId') || '',
    search: searchParams.get('search') || '',
    startDate: '',
    endDate: '',
  });
  const [cas, setCas] = useState([]); // For CA dropdown
  const [error, setError] = useState(null);

  // Fetch CAs for dropdown
  useEffect(() => {
    const fetchCas = async () => {
      try {
        const response = await bootstrapCaService.getAll({ pageSize: 100 });
        setCas(response.data || []);
      } catch (err) {
        console.error('Error fetching CAs:', err);
      }
    };
    fetchCas();
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const [logsResponse, statsResponse] = await Promise.all([
        bootstrapAuthLogsService.getAll({
          page: pagination.page,
          pageSize: pagination.pageSize,
          status: filters.status,
          caId: filters.caId,
          search: filters.search,
          startDate: filters.startDate,
          endDate: filters.endDate,
        }),
        bootstrapAuthLogsService.getStats({
          startDate: filters.startDate,
          endDate: filters.endDate,
        }),
      ]);
      
      setLogs(logsResponse.data || []);
      setStats(statsResponse.data || null);
      if (logsResponse.pagination) {
        setPagination(prev => ({ ...prev, ...logsResponse.pagination }));
      }
    } catch (err) {
      console.error('Error fetching Bootstrap Auth Logs:', err);
      setError('Failed to load authentication logs');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
    setPagination({ ...pagination, page: 1 });
  };

  const handlePageChange = (newPage) => {
    setPagination({ ...pagination, page: newPage });
  };

  const getAuthStatusBadge = (status) => {
    const statusMap = {
      success: { color: 'green', icon: '✓', label: 'Success' },
      missing: { color: 'yellow', icon: '⚠', label: 'Missing Cert' },
      expired: { color: 'red', icon: '⏱', label: 'Expired' },
      invalid_ca: { color: 'red', icon: '✗', label: 'Invalid CA' },
      unknown: { color: 'gray', icon: '?', label: 'Unknown' },
    };
    const config = statusMap[status] || statusMap.unknown;
    return (
      <span className={`badge badge-${config.color}`} title={config.label}>
        {config.icon} {config.label}
      </span>
    );
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatHardwareIds = (hardwareIds) => {
    if (!hardwareIds) return 'N/A';
    const parts = [];
    if (hardwareIds.imei) {
      parts.push(`IMEI: ${hardwareIds.imei.substring(0, 8)}...`);
    }
    if (hardwareIds.serialNumber) {
      parts.push(`SN: ${hardwareIds.serialNumber}`);
    }
    if (hardwareIds.widevineId) {
      parts.push(`WV: ${hardwareIds.widevineId.substring(0, 8)}...`);
    }
    return parts.length > 0 ? parts.join(', ') : 'N/A';
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>📋 Bootstrap Auth Logs</h1>
        <p className="page-description">
          Authentication attempts during device registration
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Stats Cards */}
      {stats && (
        <div className="stats-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <div className="stat-card" style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#333' }}>{stats.total}</div>
            <div style={{ fontSize: '0.75rem', color: '#666' }}>Total</div>
          </div>
          <div className="stat-card" style={{ background: '#d4edda', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#155724' }}>{stats.success}</div>
            <div style={{ fontSize: '0.75rem', color: '#155724' }}>Success</div>
          </div>
          <div className="stat-card" style={{ background: '#fff3cd', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#856404' }}>{stats.missing}</div>
            <div style={{ fontSize: '0.75rem', color: '#856404' }}>Missing</div>
          </div>
          <div className="stat-card" style={{ background: '#f8d7da', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#721c24' }}>{stats.invalidCa}</div>
            <div style={{ fontSize: '0.75rem', color: '#721c24' }}>Invalid CA</div>
          </div>
          <div className="stat-card" style={{ background: '#f8d7da', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#721c24' }}>{stats.expired}</div>
            <div style={{ fontSize: '0.75rem', color: '#721c24' }}>Expired</div>
          </div>
          <div className="stat-card" style={{ background: '#cce5ff', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#004085' }}>{stats.successRate}%</div>
            <div style={{ fontSize: '0.75rem', color: '#004085' }}>Success Rate</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
        <input
          type="text"
          placeholder="🔍 Search IP, hardware IDs..."
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
          className="search-input"
          style={{ minWidth: '200px' }}
        />
        
        <select
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className="filter-select"
        >
          <option value="">All Status</option>
          <option value="success">Success</option>
          <option value="missing">Missing Cert</option>
          <option value="expired">Expired</option>
          <option value="invalid_ca">Invalid CA</option>
          <option value="unknown">Unknown</option>
        </select>

        <select
          value={filters.caId}
          onChange={(e) => handleFilterChange('caId', e.target.value)}
          className="filter-select"
        >
          <option value="">All CAs</option>
          {cas.map(ca => (
            <option key={ca.id} value={ca.id}>{ca.name}</option>
          ))}
        </select>

        <input
          type="date"
          value={filters.startDate}
          onChange={(e) => handleFilterChange('startDate', e.target.value)}
          className="filter-select"
          placeholder="Start Date"
        />

        <input
          type="date"
          value={filters.endDate}
          onChange={(e) => handleFilterChange('endDate', e.target.value)}
          className="filter-select"
          placeholder="End Date"
        />

        <button 
          onClick={() => setFilters({ status: '', caId: '', search: '', startDate: '', endDate: '' })}
          className="btn btn-secondary btn-sm"
        >
          Clear Filters
        </button>
      </div>

      {/* Logs Table */}
      {loading ? (
        <div className="loading">Loading authentication logs...</div>
      ) : logs.length === 0 ? (
        <div className="empty-state">
          <p>No authentication logs found</p>
          {filters.status && <p>Try adjusting your filters</p>}
        </div>
      ) : (
        <>
          <div className="table-container" style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Status</th>
                  <th>CA</th>
                  <th>Device</th>
                  <th>Hardware IDs</th>
                  <th>IP Address</th>
                  <th>Failure Reason</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(log.createdAt)}</td>
                    <td>{getAuthStatusBadge(log.authStatus)}</td>
                    <td>
                      {log.caName ? (
                        <Link to={`/bootstrap-cas/${log.bootstrapCaId}`} title={log.caName}>
                          {log.caName.length > 20 ? log.caName.substring(0, 20) + '...' : log.caName}
                        </Link>
                      ) : (
                        <span className="text-muted">N/A</span>
                      )}
                    </td>
                    <td>
                      {log.deviceThingName ? (
                        <Link to={`/ggos-devices/${log.deviceThingName}`}>
                          🖥️ {log.deviceThingName}
                        </Link>
                      ) : (
                        <span className="text-muted">Not Registered</span>
                      )}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {formatHardwareIds(log.hardwareIds)}
                    </td>
                    <td>{log.ipAddress || 'N/A'}</td>
                    <td>
                      {log.failureReason ? (
                        <span title={log.failureReason} style={{ color: '#dc3545' }}>
                          {log.failureReason.length > 30 
                            ? log.failureReason.substring(0, 30) + '...' 
                            : log.failureReason}
                        </span>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="pagination-btn"
            >
              ← Previous
            </button>
            <span className="pagination-info">
              Page {pagination.page} of {pagination.totalPages} 
              ({pagination.totalCount} total logs)
            </span>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
              className="pagination-btn"
            >
              Next →
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default BootstrapAuthLogs;
